import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery, useQuery, useQueryClient, type InfiniteData, type QueryKey } from '@tanstack/react-query';
import { AlertCircle, ListFilter, Loader2, Plus, Search } from 'lucide-react';
import { Button } from 'components/ui/button';
import EmptyState from 'components/ui/EmptyState';
import Modal from 'components/ui/Modal';
import { TableSearch, SegmentedTabs } from 'components/ui/data-table';
import { FilterButton, FilterReset } from 'components/ui/filter-button';
import { CompactPager } from 'components/ui/pagination';
import { useToast } from 'components/ui/use-toast';
import { SettingsSectionHeader } from 'components/settings/SettingsSectionHeader';
import { useDebounce } from 'hooks/use-debounce';
import { useIsMobile } from 'hooks/use-media-query';
import { cn } from 'lib/utils';
import * as Organization from 'modules/organization';
import { useAuthContext } from 'modules/auth';
import type { OrgMember, OrgRole, PaginatedMeta } from '@examing/types';
import { PERMISSIONS } from 'modules/auth/permissions';
import { InviteMembersModal, type Recipient } from 'components/organization/InviteMembersModal';
import { ASSIGNABLE_ROLES, MEMBER_STATUSES, STATUS_LABEL_KEYS, roleLabel, type MemberActions } from './members/memberLabels';
import { MembersTable, MembersTableSkeleton } from './members/MembersTable';
import { MembersPhoneList } from './members/MembersPhoneList';
import { MemberActionsSheet } from './members/MemberActionsSheet';
import { MembersFilterSheet } from './members/MembersFilterSheet';

interface UserManagementProps {
  /** Dialog mode: the settings dialog drives drill-down via `?member=` instead of navigating. */
  onSelectMember?: (id: string) => void;
}

/** Rows per page (desktop) and per "load more" (phones): a page fits the settings dialog without scrolling. */
const PAGE_SIZE = 8;

const EMPTY_META: PaginatedMeta = { total: 0, page: 1, limit: PAGE_SIZE, totalPages: 0 };

type MembersPage = { members: OrgMember[]; meta: PaginatedMeta };

const UserManagement: React.FC<UserManagementProps> = ({ onSelectMember }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const { state: authState } = useAuthContext();
  const { toast } = useToast();
  const orgId = authState.organization?.id;

  useEffect(() => {
    if (authState.isFetched && !authState.permissions.includes(PERMISSIONS.MEMBERS_EDIT)) navigate('/app');
  }, [authState.isFetched, authState.permissions, navigate]);

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const filters = { search: debouncedSearch.trim(), role: roleFilter, status: statusFilter };
  // The page belongs to the filters it was picked under: any change to them
  // reads as page 1 straight away, so one filter change costs one request.
  const filterKey = JSON.stringify([filters.search, roleFilter, statusFilter]);
  const [paging, setPaging] = useState({ key: filterKey, page: 1 });
  const page = paging.key === filterKey ? paging.page : 1;
  const setPage = (next: number) => setPaging({ key: filterKey, page: next });

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sheetMember, setSheetMember] = useState<OrgMember | null>(null);
  const [roleChangeModal, setRoleChangeModal] = useState<{ member: OrgMember; newRole: string } | null>(null);
  const [removeModal, setRemoveModal] = useState<OrgMember | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const fetchPage = async (pageNumber: number): Promise<MembersPage> => {
    const { data: res } = await Organization.Api.GetMembers(orgId as string, {
      search: filters.search || undefined,
      role: roleFilter || undefined,
      status: statusFilter,
      page: pageNumber,
      limit: PAGE_SIZE,
    });
    const body = res as { data?: OrgMember[]; meta?: PaginatedMeta } | undefined;
    return { members: body?.data ?? [], meta: body?.meta ?? EMPTY_META };
  };

  // Desktop pages through a table; phones append pages under one list. Only the
  // one on screen fetches. Both keep the last result while the next loads,
  // never across organizations.
  const tableQuery = useQuery({
    queryKey: ['org-members', orgId, { ...filters, page, perPage: PAGE_SIZE }],
    enabled: Boolean(orgId) && !isMobile,
    placeholderData: (previous, previousQuery) => (previousQuery?.queryKey[1] === orgId ? previous : undefined),
    queryFn: () => fetchPage(page),
  });
  const listQuery = useInfiniteQuery<MembersPage, Error, InfiniteData<MembersPage, number>, QueryKey, number>({
    queryKey: ['org-members', orgId, 'infinite', filters],
    enabled: Boolean(orgId) && isMobile,
    initialPageParam: 1,
    placeholderData: (previous, previousQuery) => (previousQuery?.queryKey[1] === orgId ? previous : undefined),
    queryFn: ({ pageParam }) => fetchPage(pageParam),
    getNextPageParam: (last) => (last.meta.page < last.meta.totalPages ? last.meta.page + 1 : undefined),
  });

  const members = isMobile
    ? (listQuery.data?.pages ?? []).flatMap((p) => p.members)
    : tableQuery.data?.members ?? [];
  const meta = (isMobile ? listQuery.data?.pages[0]?.meta : tableQuery.data?.meta) ?? EMPTY_META;
  // After a member changes, refresh whichever list is on screen (every loaded page of it).
  const refetchMembers = () => queryClient.invalidateQueries({ queryKey: ['org-members', orgId] });

  // Removing the last members of the last page would otherwise strand the user on an empty page.
  useEffect(() => {
    if (meta.totalPages > 0 && page > meta.totalPages) setPaging({ key: filterKey, page: meta.totalPages });
  }, [meta.totalPages, page, filterKey]);

  const hasFilters = Boolean(search.trim() || roleFilter || statusFilter);
  const clearFilters = () => {
    setSearch('');
    setRoleFilter('');
    setStatusFilter(undefined);
  };

  const handleInvite = async (recipients: Recipient[], role: string) => {
    if (!orgId) return;
    const emails = recipients.filter((r) => r.kind === 'email');
    const phones = recipients.filter((r) => r.kind === 'phone');
    setInviteSubmitting(true);
    try {
      await Promise.all(emails.map((r) => Organization.Api.SendEmailInvite(orgId, { email: r.value, role })));
      // Org-member invites are email-only; there is no staff phone-invite path
      // (candidates onboard via team join links) — flag phones honestly.
      toast.success(
        phones.length > 0
          ? t('members_invite_phone_gap', { count: emails.length })
          : t('members_invited', { count: emails.length }),
      );
      setInviteOpen(false);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || t('failed_to_send_invitation'));
    } finally {
      setInviteSubmitting(false);
    }
  };

  const handleChangeRole = async () => {
    if (!orgId || !roleChangeModal) return;
    setActionLoading(roleChangeModal.member.id);
    try {
      await Organization.Api.UpdateMemberRole(orgId, roleChangeModal.member.id, { role: roleChangeModal.newRole });
      toast.success(t('role_updated_successfully'));
      setRoleChangeModal(null);
      refetchMembers();
    } catch {
      toast.error(t('failed_to_update_role'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleSuspendToggle = async (member: OrgMember) => {
    if (!orgId) return;
    const newStatus = member.user?.status === 'SUSPENDED' ? 'ACTIVE' : 'SUSPENDED';
    setActionLoading(member.id);
    try {
      await Organization.Api.UpdateMemberStatus(orgId, member.id, { status: newStatus });
      refetchMembers();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || t('failed_to_update_status'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemoveMember = async () => {
    if (!orgId || !removeModal) return;
    setActionLoading(removeModal.id);
    try {
      await Organization.Api.RemoveMember(orgId, removeModal.id);
      toast.success(t('member_removed_successfully'));
      setRemoveModal(null);
      refetchMembers();
    } catch {
      toast.error(t('failed_to_remove_member'));
    } finally {
      setActionLoading(null);
    }
  };

  const actions: MemberActions = {
    onView: (member) =>
      onSelectMember ? onSelectMember(member.id) : navigate(`/app/admin/organization/users/${member.id}`),
    onChangeRole: (member: OrgMember, role: OrgRole) => setRoleChangeModal({ member, newRole: role }),
    onToggleSuspend: handleSuspendToggle,
    onRemove: setRemoveModal,
  };

  const openInvite = () => setInviteOpen(true);

  const renderTableBody = () => {
    if (tableQuery.isLoading) return <MembersTableSkeleton />;
    if (tableQuery.isError) {
      return (
        <EmptyState
          icon={<AlertCircle />}
          title={t('failed_to_load_members')}
          secondaryAction={{ label: t('retry'), onClick: () => tableQuery.refetch() }}
        />
      );
    }
    if (members.length === 0) {
      return (
        <EmptyState
          icon={<Search />}
          title={t('no_members_found')}
          description={t('try_adjusting_your_search_or_filters')}
          secondaryAction={hasFilters ? { label: t('clear_filters'), onClick: clearFilters } : undefined}
        />
      );
    }
    return <MembersTable members={members} busyId={actionLoading} actions={actions} onSelectMember={onSelectMember} />;
  };

  const phoneFilterCount = (roleFilter ? 1 : 0) + (statusFilter ? 1 : 0);

  return (
    <div className="flex w-full flex-col gap-4 max-md:gap-3.5">
      {isMobile ? (
        <div className="flex items-center justify-between gap-3">
          <h2 className="flex min-w-0 items-baseline gap-1.5 text-[17px] font-semibold text-foreground">
            <span className="truncate">{t('nav.members')}</span>
            {meta.total > 0 && <span className="text-[13px] font-medium tabular-nums text-muted-foreground">{meta.total}</span>}
          </h2>
          <Button className="shrink-0 gap-1.5" onClick={openInvite}>
            <Plus className="h-4 w-4" aria-hidden />
            {t('members_invite_short')}
          </Button>
        </div>
      ) : (
        <SettingsSectionHeader
          title={t('nav.members')}
          count={meta.total > 0 ? meta.total : undefined}
          description={t('manage_team_members_roles_and_invite_new_users')}
          actions={
            <Button className="gap-1.5" onClick={openInvite}>
              <Plus className="h-4 w-4" aria-hidden />
              {t('invite_members_title')}
            </Button>
          }
        />
      )}

      {isMobile ? (
        // Phones keep only the search in the row; role and status sit in a sheet.
        <div className="flex items-center gap-2">
          <TableSearch
            value={search}
            onChange={setSearch}
            placeholder={t('members_search_short')}
            aria-label={t('members_search_short')}
            className="min-w-0 flex-1 sm:w-auto [&_input]:h-10"
          />
          <button
            type="button"
            onClick={() => setFiltersOpen(true)}
            aria-label={t('filters')}
            className="-m-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <span className="relative flex h-10 w-10 items-center justify-center rounded-md border border-border bg-card text-foreground/80">
              <ListFilter className="h-[18px] w-[18px]" aria-hidden />
              {phoneFilterCount > 0 && (
                <span aria-hidden className="absolute right-2 top-2 h-[7px] w-[7px] rounded-full bg-foreground ring-2 ring-card" />
              )}
            </span>
          </button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <TableSearch
            value={search}
            onChange={setSearch}
            placeholder={t('search_by_name_or_email')}
            aria-label={t('search_by_name_or_email')}
            className="sm:w-[220px] [&_input]:h-[34px]"
          />
          <SegmentedTabs
            aria-label={t('role')}
            value={roleFilter || 'all'}
            onChange={(v) => setRoleFilter(v === 'all' ? '' : v)}
            options={[
              { value: 'all', label: t('all_roles') },
              ...ASSIGNABLE_ROLES.map((r) => ({ value: r.value as string, label: t(r.labelKey) })),
            ]}
            className="h-[34px] [&>button]:text-[12.5px]"
          />
          <FilterButton
            label={t('status')}
            options={MEMBER_STATUSES.map((value) => ({ value, label: t(STATUS_LABEL_KEYS[value]) }))}
            value={statusFilter}
            onChange={setStatusFilter}
            className="h-[34px]"
          />
          {hasFilters && <FilterReset onClick={clearFilters} className="h-[34px]" />}
        </div>
      )}

      {isMobile ? (
        <MembersPhoneList
          members={members}
          total={meta.total}
          step={PAGE_SIZE}
          isLoading={listQuery.isLoading}
          isError={listQuery.isError}
          isStale={listQuery.isPlaceholderData}
          hasFilters={hasFilters}
          onRetry={() => listQuery.refetch()}
          onClearFilters={clearFilters}
          onLoadMore={() => listQuery.fetchNextPage()}
          loadingMore={listQuery.isFetchingNextPage}
          busyId={actionLoading}
          onOpenMember={setSheetMember}
        />
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-card">
          {/* While the next page loads the previous one stays, dimmed, so nothing jumps. */}
          <div
            className={cn('transition-opacity', tableQuery.isPlaceholderData && 'opacity-60')}
            aria-busy={tableQuery.isPlaceholderData || undefined}
          >
            {renderTableBody()}
          </div>
          {!tableQuery.isLoading && !tableQuery.isError && meta.total > 0 && (
            <div className="border-t border-border px-4 py-3">
              <CompactPager page={page} pageCount={meta.totalPages || 1} pageSize={PAGE_SIZE} total={meta.total} onPageChange={setPage} />
            </div>
          )}
        </div>
      )}

      {isMobile && (
        <>
          <MembersFilterSheet
            open={filtersOpen}
            onClose={() => setFiltersOpen(false)}
            role={roleFilter}
            onRoleChange={setRoleFilter}
            status={statusFilter}
            onStatusChange={setStatusFilter}
            total={meta.total}
          />
          <MemberActionsSheet member={sheetMember} onClose={() => setSheetMember(null)} actions={actions} />
        </>
      )}

      <InviteMembersModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        title={t('invite_members_title')}
        roles={[
          { value: 'NO_ACCESS', label: t('role_no_access'), description: t('members_role_no_access_desc') },
          { value: 'AUTHOR', label: t('role_author'), description: t('members_role_author_desc') },
          { value: 'PROCTOR', label: t('role_proctor'), description: t('members_role_proctor_desc') },
          { value: 'ORG_ADMIN', label: t('role_admin'), description: t('members_role_admin_desc') },
        ]}
        defaultRole="NO_ACCESS"
        infoText={t('members_invite_info')}
        submitting={inviteSubmitting}
        onSubmit={handleInvite}
      />

      <Modal isOpen={!!roleChangeModal} onClose={() => setRoleChangeModal(null)} title={t('change_member_role')} size="sm">
        {roleChangeModal && (
          <div className="space-y-4">
            <p className="text-muted-foreground">
              {t('change')} <strong className="text-foreground">{roleChangeModal.member.user?.name}</strong> {t('s_role_to')}{' '}
              <strong className="text-foreground">{roleLabel(roleChangeModal.newRole, t)}</strong>?
            </p>
            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <Button className="w-full sm:w-auto" variant="outline" onClick={() => setRoleChangeModal(null)}>{t('cancel')}</Button>
              <Button className="w-full sm:w-auto" onClick={handleChangeRole} disabled={actionLoading === roleChangeModal.member.id}>
                {actionLoading === roleChangeModal.member.id && <Loader2 size={16} className="mr-2 animate-spin" />}{t('confirm')}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal isOpen={!!removeModal} onClose={() => setRemoveModal(null)} title={t('remove_member')} size="sm">
        {removeModal && (
          <div className="space-y-4">
            <p className="text-muted-foreground">
              {t('are_you_sure_you_want_to_remove')} <strong className="text-foreground">{removeModal.user?.name}</strong> {t('from_this_organization_this_action_cannot_be_undone')}
            </p>
            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <Button className="w-full sm:w-auto" variant="outline" onClick={() => setRemoveModal(null)}>{t('cancel')}</Button>
              <Button className="w-full sm:w-auto" variant="destructive" onClick={handleRemoveMember} disabled={actionLoading === removeModal.id}>
                {actionLoading === removeModal.id && <Loader2 size={16} className="mr-2 animate-spin" />}{t('remove')}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default UserManagement;
